import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { withStyles } from '@material-ui/core/styles';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  FormControlLabel,
  CardHeader,
  Typography,
  IconButton,
  Switch,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import ChatBubbleIcon from '@material-ui/icons/ChatBubble';
import { get, map, head, last } from 'lodash';
import moment from 'moment';

import { makeSelectCurrentUser } from 'containers/App/selectors';
import { loadEventMessages } from 'containers/PatientPage/actions';
import MessagesArea from 'components/Chat/MessagesArea';

const styles = (theme) => ({
  paper: {
    minHeight: 'calc(100% - 96px)',
  },
  header: {
    height: 70,
    backgroundColor: theme.palette.text.primary,
    color: 'hsl(theme.palette.common.white, 50%)',
  },
  dialogTitle: {
    padding: 0,
  },
  title: {
    color: theme.palette.common.white,
  },
  content: {
    width: 600,
    padding: 0,
    paddingTop: theme.spacing.unit * 2,
  },
  flex: {
    flex: 1,
  },
  action: {
    marginTop: -1 * theme.spacing.unit * 0.5,
    marginRight: -1 * theme.spacing.unit * 1.5,
    '& span': {
      color: 'hsl(theme.palette.common.white, 50%)',
    },
  },
});

class PatientJourneyCardDialogMessages extends Component {
  state = {
    includeOtherMessages: false,
  };

  componentDidUpdate(prevProps) {
    const { open } = this.props;
    if (open !== prevProps.open) {
      this.handleReset();
    }
  }

  handleReset = () => {
    this.setState({ includeOtherMessages: false });
  };

  getEventMessages = () => {
    const { event } = this.props;
    return get(event, 'details.messages.data');
  };

  handleIncludeOtherMessagesChange = (event, includeOtherMessages) => {
    const { patient, dispatchLoadEventMessages } = this.props;
    this.setState({ includeOtherMessages });
    if (includeOtherMessages) {
      // include non-event specific messages that are timeboxed to
      // 1 week before the first message and 1 week after the last message
      const eventMessages = this.getEventMessages();
      const fromDate = moment(get(head(eventMessages), 'createdAt')).subtract(7, 'days');
      const toDate = moment(get(last(eventMessages), 'createdAt')).add(7, 'days');
      dispatchLoadEventMessages({
        patient: patient._id,
        createdAt: [`>=${fromDate}`, `<=${toDate}`],
      });
    }
  };

  render() {
    const { classes, singleline, open, currentUser, event, patient, permissions, onClose } = this.props;
    const { includeOtherMessages } = this.state;
    const eventMessages = this.getEventMessages();

    const messages = map(eventMessages, (message) => ({
      ...message,
      icon: event.type,
    }));
    return (
      <Dialog
        classes={{
          paper: classes.paper,
        }}
        open={open}
        onClose={onClose}
        maxWidth={false}
      >
        <DialogTitle className={classes.dialogTitle}>
          <CardHeader
            classes={{
              root: classes.header,
              action: classes.action,
            }}
            avatar={<ChatBubbleIcon />}
            title={<Typography className={classes.title} variant="h6">{`Chat with ${patient.name}`}</Typography>}
            action={
              <IconButton onClick={onClose}>
                <CloseIcon />
              </IconButton>
            }
          />
        </DialogTitle>
        <Divider />
        <DialogContent className={classes.content}>
          <MessagesArea currentUser={currentUser} messages={messages} patient={patient} permissions={permissions} />
        </DialogContent>
        <DialogActions>
          {!singleline && (
            <FormControlLabel
              className={classes.flex}
              control={
                <Switch
                  checked={includeOtherMessages}
                  color="primary"
                  onChange={this.handleIncludeOtherMessagesChange}
                />
              }
              label="Include other chat messages"
            />
          )}
          <Button color="primary" variant="contained" onClick={onClose}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}

PatientJourneyCardDialogMessages.propTypes = {
  classes: PropTypes.object.isRequired,
  open: PropTypes.bool.isRequired,
  currentUser: PropTypes.object.isRequired,
  patient: PropTypes.object,
  permissions: PropTypes.object.isRequired,
  event: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  dispatchLoadEventMessages: PropTypes.func.isRequired,
};

const mapStateToProps = createStructuredSelector({
  currentUser: makeSelectCurrentUser(),
});

const mapDispatchToProps = {
  dispatchLoadEventMessages: loadEventMessages,
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withStyles(styles),
)(PatientJourneyCardDialogMessages);
